/**
 * Детекция нарушений на локальном видео (CONTRACT §5).
 *
 *   import { loadFaceLandmarker, createViolationMonitor, captureFrameJpeg, buildViolationFrame } from '@/services/faceDetection'
 *
 *   preloadFaceDetectionCode()                 // после входа: заранее подтянуть JS-чанк MediaPipe (без модели)
 *   await loadFaceLandmarker()                 // singleton на всю сессию приложения (GPU → CPU фолбэк)
 *   const monitor = createViolationMonitor({
 *     video, mode: 'BLINK' | 'MOUTH',
 *     onFace: (present) => …,                  // true сразу; false — лица нет > 1.5 c
 *     onMetrics: (m) => …,                     // {blinkScore, ear, jawOpen, mar, face}
 *     onViolation: (v) => …,                   // {detectionType, score, metrics, timestamp, jpeg: Promise<Blob>}
 *     onError: (e) => …,                       // детектор стабильно падает
 *   })
 *   monitor.start()                            // цикл requestVideoFrameCallback (rVFC замолчал или его нет — rAF)
 *   const calib = await monitor.calibrate(4600) // базовая линия (медианы) → пороги
 *   monitor.arm()                              // включить срабатывание нарушений
 *   monitor.disarm(); monitor.stop()           // модель остаётся загруженной для следующей игры
 *
 * Пороги и число кадров — по CONTRACT §5 (см. DETECTION ниже). Уточнение для BLINK: если между двумя
 * последними обработанными кадрами > 50 мс (медленное устройство, < 20 fps), моргание длиной 70-150 мс
 * попадает максимум в один кадр — тогда нарушение засчитывается по одному кадру с сильными признаками
 * (blinkScore ≥ порога И ear < 0.15). MOUTH — нарушение, наоборот, ЗАКРЫТЫЙ рот (3 кадра подряд); после arm()
 * до первого кадра с открытым ртом есть фора MOUTH_ARM_GRACE_MS — успеть открыть рот после старта/паузы.
 * Нарушение: текущий кадр видео рисуется в canvas сразу в том же колбэке, кодируется в JPEG
 * (≤ 640 px по длинной стороне, качество 0.7), цикл детекции останавливается.
 */

import diagnostics from "@/services/diagnostics";

const BASE_URL = process.env.BASE_URL || "/";
const WASM_PATH = `${BASE_URL}mediapipe/wasm`;
const MODEL_PATH = `${BASE_URL}mediapipe/face_landmarker.task`;

export const DETECTION = Object.freeze({
  /** Подряд обработанных кадров для нарушения. */
  BLINK_FRAMES: 2,
  MOUTH_FRAMES: 3,
  /**
   * BLINK: интервал между двумя последними обработанными кадрами больше этого — достаточно одного кадра
   * с сильными признаками (blinkScore ≥ порога И ear < EAR_CLOSED).
   */
  BLINK_SINGLE_FRAME_GAP_MS: 50,
  /** Пороги без калибровки. */
  BLINK_DEFAULT: 0.55,
  /**
   * MOUTH: рот закрыт — jawOpen < 0.35 И mar < MAR_CLOSED. Порог не калибруется (на отсчёте рот уже открыт);
   * jawOpen страхует от ложного «закрыт», когда MAR занижен (наклон головы), а челюсть явно опущена.
   */
  MOUTH_DEFAULT: 0.35,
  /** blinkThreshold = clamp(baseBlink + 0.35, 0.45, 0.8) */
  BLINK_OFFSET: 0.35,
  BLINK_MIN: 0.45,
  BLINK_MAX: 0.8,
  /** BLINK: ear < 0.15 при blinkScore ≥ blinkThreshold − 0.1 */
  EAR_CLOSED: 0.15,
  EAR_MARGIN: 0.1,
  /** MOUTH: губы сомкнуты — mar < 0.08 */
  MAR_CLOSED: 0.08,
  /** MOUTH: после arm() закрытый рот не засчитывается, пока рот ни разу не открылся, но не дольше этого. */
  MOUTH_ARM_GRACE_MS: 2000,
  /** Нет лица дольше — предупреждение «Лицо не в кадре». */
  FACE_LOST_MS: 1500,
  /** Калибровка: минимум кадров и охват по времени. */
  CALIBRATION_MIN_MS: 2000,
  CALIBRATION_MIN_SAMPLES: 8,
  /** Рабочий canvas для детекции (длинная сторона). */
  PROCESS_LONG_SIDE: 320,
  /** Не чаще ~30 fps (минус допуск на джиттер, см. FRAME_JITTER_MS). */
  MIN_FRAME_INTERVAL_MS: 33,
  /** Допуск на джиттер колбэков: кадры камеры (33.3 мс) и vsync 90/120 Гц не должны пропускаться. */
  FRAME_JITTER_MS: 6,
  /** Минимальный интервал не меньше frameCost × это: на медленных устройствах главный поток не забивается. */
  FRAME_COST_FACTOR: 1.25,
  /** Колбэков цикла нет дольше — rVFC «замолчал»: переключаемся на rAF. */
  LOOP_STALL_MS: 500,
  /** Кадр нарушения. */
  CAPTURE_LONG_SIDE: 640,
  JPEG_QUALITY: 0.7,
  /** Загрузка модели дольше — ошибка (экран «Повторить / Уйти» вместо вечной «Подготовки…»). */
  LOAD_TIMEOUT_MS: 60000,
});

// Индексы landmark'ов FaceMesh: [p1, p2, p3, p4, p5, p6] для EAR = (|p2-p6| + |p3-p5|) / (2|p1-p4|).
const LEFT_EYE = [33, 160, 158, 133, 153, 144];
const RIGHT_EYE = [362, 385, 387, 263, 373, 380];
// MAR = mean(|13-14|, |82-87|, |312-317|) / |78-308|
const MOUTH_PAIRS = [
  [13, 14],
  [82, 87],
  [312, 317],
];
const MOUTH_CORNERS = [78, 308];

const isDev = process.env.NODE_ENV === "development";

/* ------------------------------------------------------- модель (singleton) */

const model = {
  instance: null,
  delegate: null,
  vision: null,
  fileset: null,
  loading: null,
  switching: null,
  lastTs: 0,
};

/**
 * Свой DOM-canvas на КАЖДЫЙ экземпляр (GPU и CPU): без него MediaPipe в WKWebView iOS 16.4-16.7
 * (UA без «Safari») берёт OffscreenCanvas, у которого там нет WebGL, — и не работает ни один делегат.
 * Общий canvas нельзя: у canvas один WebGL-контекст, а CPU-экземпляр создаётся до закрытия GPU-экземпляра.
 */
function landmarkerOptions(delegate) {
  return {
    canvas: typeof document !== "undefined" ? document.createElement("canvas") : undefined,
    baseOptions: { modelAssetPath: MODEL_PATH, delegate },
    runningMode: "VIDEO",
    numFaces: 1,
    outputFaceBlendshapes: true,
    outputFacialTransformationMatrixes: false,
  };
}

async function createInstance(delegate) {
  return model.vision.FaceLandmarker.createFromOptions(model.fileset, landmarkerOptions(delegate));
}

diagnostics.registerSource("model", () => ({ loaded: !!model.instance, delegate: model.delegate,
  loading: !!model.loading, switching: !!model.switching }));

let visionImport = null;

/** JS-чанк MediaPipe (единственное место импорта; prefetch-подсказка — при старте приложения). */
function importVision() {
  if (model.vision) return Promise.resolve(model.vision);
  if (!visionImport) {
    visionImport = import(/* webpackChunkName: "mediapipe", webpackPrefetch: true */ "@mediapipe/tasks-vision")
      .then((vision) => {
        model.vision = vision;
        return vision;
      })
      .finally(() => {
        visionImport = null;
      });
  }
  return visionImport;
}

/**
 * Не загрузился JS-чанк (обычно после деплоя: старый хэш чанка удалён с сервера). Повтор запросит тот же
 * несуществующий файл — помогает только перезагрузка приложения.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  return error.code === "CHUNK_LOAD_FAILED" || error.name === "ChunkLoadError" || /Loading chunk .* failed/i.test(error.message || "");
}

async function doLoad() {
  if (!model.vision) await importVision();
  if (!model.fileset) {
    model.fileset = await model.vision.FilesetResolver.forVisionTasks(WASM_PATH);
  }
  let instance = null;
  let delegate = "GPU";
  try {
    instance = await createInstance("GPU");
  } catch (error) {
    if (isDev) console.warn("[faceDetection] GPU delegate недоступен, используем CPU", error);
    delegate = "CPU";
    instance = await createInstance("CPU");
  }
  model.instance = instance;
  model.delegate = delegate;
  diagnostics.event("delegate", { delegate });
  model.lastTs = 0;
  return instance;
}

function withTimeout(promise, ms) {
  if (!ms) return promise;
  let timer = null;
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      reject(Object.assign(new Error("Модель распознавания загружается слишком долго"), { code: "MODEL_TIMEOUT" }));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Загрузить FaceLandmarker один раз за сессию приложения (повторные вызовы — тот же экземпляр).
 * Ошибка загрузки не кэшируется: следующий вызов (кнопка «Повторить») пробует снова.
 * @param {{timeoutMs?: number}} [options]
 * @returns {Promise<object>} экземпляр FaceLandmarker
 */
export function loadFaceLandmarker({ timeoutMs = DETECTION.LOAD_TIMEOUT_MS } = {}) {
  if (model.instance) return Promise.resolve(model.instance);
  if (!model.loading) {
    model.loading = doLoad()
      .catch((error) => {
        throw Object.assign(error instanceof Error ? error : new Error(String(error)), {
          code: (error && error.code) || (isChunkLoadError(error) ? "CHUNK_LOAD_FAILED" : "MODEL_LOAD_FAILED"),
        });
      })
      .finally(() => {
        model.loading = null;
      });
  }
  // Таймаут не отменяет загрузку: повторный вызов дождётся той же загрузки с новым таймаутом.
  return withTimeout(model.loading, timeoutMs);
}

/**
 * Заранее (сразу после входа) загрузить только JS-код MediaPipe, без модели и wasm: модуль остаётся в
 * памяти, и игра не зависит от того, есть ли ещё этот чанк на сервере после деплоя.
 * Ошибки игнорируются — loadFaceLandmarker() повторит импорт.
 */
export function preloadFaceDetectionCode() {
  importVision().catch(() => {
    // повторит loadFaceLandmarker
  });
}

/** Начать загрузку заранее (экран поиска). Ошибки игнорируются — экран игры повторит загрузку. */
export function preloadFaceLandmarker() {
  loadFaceLandmarker({ timeoutMs: 0 }).catch(() => {
    // обработает экран игры
  });
}

/** GPU-делегат падает на кадрах (бывает в WebView) → пересоздаём на CPU, один раз. */
function switchToCpu() {
  if (model.delegate !== "GPU" || !model.vision || !model.fileset) return Promise.resolve(false);
  if (!model.switching) {
    const previous = model.instance;
    model.switching = createInstance("CPU")
      .then((instance) => {
        model.instance = instance;
        model.delegate = "CPU";
        diagnostics.event("delegate", { delegate: "CPU" });
        model.lastTs = 0;
        try {
          if (previous) previous.close();
        } catch (e) {
          // старый экземпляр уже освобождён
        }
        if (isDev) console.warn("[faceDetection] переключились на CPU-делегат");
        return true;
      })
      .catch(() => false)
      .finally(() => {
        model.switching = null;
      });
  }
  return model.switching;
}

/** VIDEO-режим требует строго возрастающих timestamp'ов на одном экземпляре (общем для всех игр). */
function nextTimestamp(now) {
  const ts = Math.max(Math.round(now), model.lastTs + 1);
  model.lastTs = ts;
  return ts;
}

/* -------------------------------------------------------------- canvas'ы */

let workCanvas = null;
let workCtx = null;
let captureCanvas = null;

function getWorkCanvas() {
  if (!workCanvas) {
    workCanvas = document.createElement("canvas");
    workCtx = workCanvas.getContext("2d", { alpha: false });
  }
  return workCanvas;
}

function getCaptureCanvas() {
  if (!captureCanvas) captureCanvas = document.createElement("canvas");
  return captureCanvas;
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      // toBlob копирует растр синхронно в момент вызова — canvas можно сразу переиспользовать.
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Не удалось закодировать JPEG"))), "image/jpeg", quality);
      return;
    }
    try {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      const binary = atob(dataUrl.split(",")[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      resolve(new Blob([bytes], { type: "image/jpeg" }));
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Снять ТЕКУЩИЙ кадр видео (синхронно) и закодировать в JPEG ≤ 640 px по длинной стороне, качество 0.7.
 * @param {HTMLVideoElement} video
 * @returns {Promise<Blob>}
 */
export function captureFrameJpeg(video, { maxSide = DETECTION.CAPTURE_LONG_SIDE, quality = DETECTION.JPEG_QUALITY } = {}) {
  const vw = video && video.videoWidth;
  const vh = video && video.videoHeight;
  if (!vw || !vh) return Promise.reject(Object.assign(new Error("Нет кадра видео"), { code: "NO_FRAME" }));
  const scale = Math.min(1, maxSide / Math.max(vw, vh));
  const canvas = getCaptureCanvas();
  canvas.width = Math.max(1, Math.round(vw * scale));
  canvas.height = Math.max(1, Math.round(vh * scale));
  canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvasToJpeg(canvas, quality);
}

/**
 * Бинарный кадр нарушения (CONTRACT §3.3): `<UTF-8 JSON>\n<JPEG>`.
 * @param {{detectionType:'BLINK'|'MOUTH', gameUUID:string, sessionTime:number, timestamp?:number, score?:number}} meta
 * @param {Blob} jpeg
 * @returns {Blob}
 */
export function buildViolationFrame(meta, jpeg) {
  const detectionType = meta.detectionType === "MOUTH" ? "MOUTH" : "BLINK";
  const metadata = {
    type: detectionType === "MOUTH" ? "detected_mouth_open" : "detected_blink",
    detectionType,
    gameUUID: meta.gameUUID,
    sessionTime: Math.max(0, Math.round(Number(meta.sessionTime) || 0)),
    timestamp: meta.timestamp || Date.now(),
    score: Math.round((Number(meta.score) || 0) * 1000) / 1000,
    blink: detectionType === "BLINK",
  };
  return new Blob([`${JSON.stringify(metadata)}\n`, jpeg], { type: "application/octet-stream" });
}

/* --------------------------------------------------------------- метрики */

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function dist(a, b, w, h) {
  const dx = (a.x - b.x) * w;
  const dy = (a.y - b.y) * h;
  return Math.sqrt(dx * dx + dy * dy);
}

/** EAR в пиксельных координатах canvas. */
function eyeAspectRatio(lm, idx, w, h) {
  const [p1, p2, p3, p4, p5, p6] = idx.map((i) => lm[i]);
  if (!p1 || !p2 || !p3 || !p4 || !p5 || !p6) return null;
  const horizontal = dist(p1, p4, w, h);
  if (horizontal < 1e-6) return null;
  return (dist(p2, p6, w, h) + dist(p3, p5, w, h)) / (2 * horizontal);
}

/** MAR в пиксельных координатах canvas. */
function mouthAspectRatio(lm, w, h) {
  const left = lm[MOUTH_CORNERS[0]];
  const right = lm[MOUTH_CORNERS[1]];
  if (!left || !right) return null;
  const width = dist(left, right, w, h);
  if (width < 1e-6) return null;
  let sum = 0;
  for (const [a, b] of MOUTH_PAIRS) {
    if (!lm[a] || !lm[b]) return null;
    sum += dist(lm[a], lm[b], w, h);
  }
  return sum / MOUTH_PAIRS.length / width;
}

function readBlendshapes(result) {
  const out = { blinkLeft: 0, blinkRight: 0, jawOpen: 0 };
  const group = result.faceBlendshapes && result.faceBlendshapes[0];
  if (!group || !group.categories) return out;
  for (const c of group.categories) {
    if (c.categoryName === "eyeBlinkLeft") out.blinkLeft = c.score;
    else if (c.categoryName === "eyeBlinkRight") out.blinkRight = c.score;
    else if (c.categoryName === "jawOpen") out.jawOpen = c.score;
  }
  return out;
}

/** Пороги по базовой линии (CONTRACT §5). baseline = null → значения по умолчанию. MOUTH не калибруется. */
export function thresholdsFor(baseline) {
  if (!baseline) return { blink: DETECTION.BLINK_DEFAULT, mouth: DETECTION.MOUTH_DEFAULT };
  return {
    blink: clamp(baseline.blink + DETECTION.BLINK_OFFSET, DETECTION.BLINK_MIN, DETECTION.BLINK_MAX),
    mouth: DETECTION.MOUTH_DEFAULT,
  };
}

/* ------------------------------------------------------------- монитор */

/**
 * Монитор нарушений для одного <video> (локальная камера). Модель — общий singleton.
 * @param {object} options
 * @param {HTMLVideoElement} options.video
 * @param {'BLINK'|'MOUTH'} options.mode
 * @param {(metrics:object)=>void} [options.onMetrics]
 * @param {(present:boolean)=>void} [options.onFace]
 * @param {(violation:object)=>void} [options.onViolation]
 * @param {(error:Error)=>void} [options.onError]
 */
export function createViolationMonitor(options) {
  const { video } = options;
  const detectionType = options.mode === "MOUTH" ? "MOUTH" : "BLINK";
  const framesNeeded = detectionType === "MOUTH" ? DETECTION.MOUTH_FRAMES : DETECTION.BLINK_FRAMES;

  const call = (name, payload) => {
    const fn = options[name];
    if (typeof fn !== "function") return;
    try {
      fn(payload);
    } catch (error) {
      console.error(`[faceDetection] ${name} failed`, error);
    }
  };

  let running = false;
  let releaseDiagnostics = null;
  let armed = false;
  let busy = false;
  let rvfcHandle = null;
  let rafHandle = null;
  let kickTimer = null;
  let lastProcessed = 0; // 0 — после start() кадров ещё не было
  let lastGap = Infinity; // интервал между двумя последними обработанными кадрами, мс
  let lastCallback = 0;
  let frameCost = 0; // скользящее среднее времени обработки кадра, мс (0 — ещё не измерено)
  let costInstance = null; // экземпляр модели, для которого считается frameCost
  let consecutive = 0;
  let armedAt = 0;
  let mouthSeenOpen = false; // MOUTH: после arm() уже был кадр с открытым ртом (фора кончилась)
  let errorCount = 0;
  let lastFaceAt = 0;
  let facePresent = false;
  let reportedFace = null;
  let calibration = null;
  let baseline = null;
  let thresholds = thresholdsFor(null);
  let calibrated = false;

  const useRvfc = !!video && typeof video.requestVideoFrameCallback === "function";
  /** 'rvfc' — по кадрам камеры; 'raf' — по кадрам экрана (нет rVFC или он перестал вызываться). */
  let loop = useRvfc ? "rvfc" : "raf";

  function reportFace(present) {
    if (reportedFace === present) return;
    reportedFace = present;
    call("onFace", present);
  }

  function schedule() {
    if (!running) return;
    if (loop === "rvfc") {
      if (rvfcHandle === null) rvfcHandle = video.requestVideoFrameCallback(onVideoFrame);
    } else if (rafHandle === null) {
      rafHandle = requestAnimationFrame(onAnimationFrame);
    }
  }

  function onVideoFrame(now) {
    rvfcHandle = null;
    if (!running) return;
    if (loop !== "rvfc") {
      // rVFC снова вызывается — возвращаемся с rAF на кадры камеры.
      loop = "rvfc";
      if (rafHandle !== null) cancelAnimationFrame(rafHandle);
      rafHandle = null;
    }
    lastCallback = performance.now();
    tick(now);
    schedule();
  }

  function onAnimationFrame(now) {
    rafHandle = null;
    if (!running) return;
    lastCallback = performance.now();
    tick(now);
    schedule();
  }

  /**
   * Сторож цикла (каждые 250 мс): rVFC не вызывается для невидимого/непроигрываемого видео, а в некоторых
   * WebView «замолкает» и для видимого. Колбэков нет > LOOP_STALL_MS → цикл переходит на rAF (тот же
   * темп ~30 fps; ожидающий запрос rVFC остаётся — когда он сработает, вернёмся на него).
   * Крайний случай — не приходит и rAF (страница не отрисовывается, но не hidden): тогда кадр по таймеру.
   */
  function kick() {
    if (!running) return;
    const now = performance.now();
    if (now - lastCallback <= DETECTION.LOOP_STALL_MS) return;
    if (loop === "rvfc") {
      if (isDev) console.warn("[faceDetection] requestVideoFrameCallback не вызывается — переходим на rAF");
      loop = "raf";
      lastCallback = now;
      schedule();
      return;
    }
    tick(now);
  }

  function resetFace(now) {
    consecutive = 0;
    facePresent = false;
    if (now - lastFaceAt > DETECTION.FACE_LOST_MS) reportFace(false);
  }

  function tick(now) {
    if (!running || busy) return;
    // Каждый кадр камеры 30 fps, пока кадр обходится ≤ ~22 мс; дороже — не больше ~80% главного потока.
    // Допуск на джиттер: колбэки по кадрам 33.3 мс (или vsync 90/120 Гц) не должны терять кадр.
    const minGap = Math.max(DETECTION.MIN_FRAME_INTERVAL_MS - DETECTION.FRAME_JITTER_MS, frameCost * DETECTION.FRAME_COST_FACTOR);
    if (lastProcessed && now - lastProcessed < minGap) return;
    if (typeof document !== "undefined" && document.hidden) {
      consecutive = 0;
      lastProcessed = 0; // после возврата интервал до предыдущего кадра неизвестен
      return;
    }
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    const instance = model.instance;
    if (!instance || model.switching) return;
    busy = true;
    lastGap = lastProcessed ? now - lastProcessed : Infinity;
    lastProcessed = now;
    // Первый вызов на экземпляре (после start(), загрузки или переключения на CPU) — прогрев шейдеров/wasm:
    // в среднее его не берём, иначе темп занижен на несколько секунд.
    const warmup = instance !== costInstance;
    if (warmup) {
      costInstance = instance;
      frameCost = 0;
    }
    const startedAt = performance.now();
    const profileStarted = diagnostics.enabled ? startedAt : null;
    diagnostics.metric("detection.gap", lastGap);
    try {
      processFrame(instance, now);
      errorCount = 0;
    } catch (error) {
      errorCount += 1;
      diagnostics.metric("detection.errors", 1);
      consecutive = 0;
      if (isDev) console.warn("[faceDetection] detectForVideo failed", error);
      if (errorCount >= 3 && model.delegate === "GPU") {
        errorCount = 0;
        switchToCpu();
      } else if (errorCount >= 30) {
        errorCount = 0;
        call("onError", error);
      }
    } finally {
      diagnostics.elapsed("detection.frame", profileStarted);
      if (!warmup) {
        const cost = performance.now() - startedAt;
        frameCost = frameCost ? frameCost * 0.8 + cost * 0.2 : cost;
      }
      busy = false;
    }
  }

  function processFrame(instance, now) {
    const canvas = getWorkCanvas();
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = DETECTION.PROCESS_LONG_SIDE / Math.max(vw, vh);
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    workCtx.drawImage(video, 0, 0, w, h);

    const inferenceAt = diagnostics.now();
    let result;
    try { result = instance.detectForVideo(canvas, nextTimestamp(performance.now())); }
    finally { diagnostics.elapsed("detection.inference", inferenceAt); }
    const landmarks = result && result.faceLandmarks && result.faceLandmarks[0];
    if (!landmarks || !landmarks.length) {
      resetFace(now);
      call("onMetrics", { face: false });
      return;
    }

    const shapes = readBlendshapes(result);
    const blinkScore = (shapes.blinkLeft + shapes.blinkRight) / 2;
    const earLeft = eyeAspectRatio(landmarks, LEFT_EYE, w, h);
    const earRight = eyeAspectRatio(landmarks, RIGHT_EYE, w, h);
    const ear = earLeft !== null && earRight !== null ? (earLeft + earRight) / 2 : earLeft !== null ? earLeft : earRight;
    const mar = mouthAspectRatio(landmarks, w, h);
    const jawOpen = shapes.jawOpen;

    facePresent = true;
    lastFaceAt = now;
    reportFace(true);

    const gapMs = Number.isFinite(lastGap) ? lastGap : null;
    const metrics = { face: true, blinkScore, ear, jawOpen, mar, thresholds, calibrated, frameMs: frameCost, gapMs };
    call("onMetrics", metrics);

    if (calibration) {
      calibration.samples.push({ t: now, blink: blinkScore, jaw: jawOpen, mar: mar === null ? 0 : mar });
    }

    if (!armed) return;
    let hit;
    let confirmed = false;
    if (detectionType === "BLINK") {
      const eyesClosed = ear !== null && ear < DETECTION.EAR_CLOSED;
      hit = blinkScore >= thresholds.blink || (eyesClosed && blinkScore >= thresholds.blink - DETECTION.EAR_MARGIN);
      // Редкие кадры (> 50 мс между ними): короткое моргание попадёт максимум в один кадр — достаточно
      // одного кадра с сильными признаками (оба условия сразу).
      confirmed =
        Number.isFinite(lastGap) && lastGap > DETECTION.BLINK_SINGLE_FRAME_GAP_MS && blinkScore >= thresholds.blink && eyesClosed;
    } else {
      hit = mar !== null && mar < DETECTION.MAR_CLOSED && jawOpen < thresholds.mouth;
      if (!hit) mouthSeenOpen = true;
      else if (!mouthSeenOpen && now - armedAt < DETECTION.MOUTH_ARM_GRACE_MS) hit = false;
    }
    consecutive = hit ? consecutive + 1 : 0;
    if (!confirmed && consecutive < framesNeeded) return;

    // Нарушение: снимаем текущий кадр сразу, в этом же колбэке, и останавливаем детекцию.
    consecutive = 0;
    armed = false;
    const jpeg = captureFrameJpeg(video);
    jpeg.catch(() => {
      // ошибку обработает получатель onViolation
    });
    stop();
    const score = detectionType === "BLINK" ? blinkScore : clamp(1 - Math.max(jawOpen, mar), 0, 1);
    call("onViolation", { detectionType, score, metrics, timestamp: Date.now(), mediaTime: video.currentTime, jpeg,
      regions: replayRegionsFromLandmarks(landmarks) });
  }

  function finishCalibration() {
    const current = calibration;
    if (!current) return;
    calibration = null;
    clearTimeout(current.timer);
    const samples = current.samples;
    const span = samples.length > 1 ? samples[samples.length - 1].t - samples[0].t : 0;
    if (samples.length >= DETECTION.CALIBRATION_MIN_SAMPLES && span >= DETECTION.CALIBRATION_MIN_MS) {
      baseline = {
        blink: median(samples.map((s) => s.blink)),
        jaw: median(samples.map((s) => s.jaw)),
        mar: median(samples.map((s) => s.mar)),
        samples: samples.length,
        spanMs: Math.round(span),
      };
      calibrated = true;
    } else {
      baseline = null;
      calibrated = false;
    }
    thresholds = thresholdsFor(baseline);
    current.resolve({ calibrated, baseline, thresholds: { ...thresholds } });
  }

  /** Запустить цикл обработки кадров (идемпотентно). */
  function start() {
    if (running || !video) return;
    running = true;
    busy = false;
    consecutive = 0;
    lastProcessed = 0;
    lastGap = Infinity;
    costInstance = null;
    frameCost = 0;
    loop = useRvfc ? "rvfc" : "raf";
    lastFaceAt = performance.now();
    lastCallback = performance.now();
    reportedFace = null;
    schedule();
    kickTimer = setInterval(kick, 250);
    releaseDiagnostics = diagnostics.registerSource("detector", () => ({ running, armed, loop,
      callbacks: Number(rvfcHandle !== null) + Number(rafHandle !== null),
      timers: Number(kickTimer !== null) + Number(!!calibration) }));
  }

  /** Остановить цикл (модель не выгружается). Незавершённая калибровка завершается. */
  function stop() {
    releaseDiagnostics?.(); releaseDiagnostics = null;
    running = false;
    armed = false;
    consecutive = 0;
    if (rvfcHandle !== null && video && typeof video.cancelVideoFrameCallback === "function") {
      video.cancelVideoFrameCallback(rvfcHandle);
    }
    if (rafHandle !== null) cancelAnimationFrame(rafHandle);
    rvfcHandle = null;
    rafHandle = null;
    clearInterval(kickTimer);
    kickTimer = null;
    finishCalibration();
  }

  /**
   * Калибровка: собирает кадры с лицом ms миллисекунд, базовая линия — медианы blinkScore/jawOpen/mar.
   * Меньше 2 c кадров → пороги по умолчанию (0.55 / 0.35); порог MOUTH от калибровки не зависит.
   * @returns {Promise<{calibrated:boolean, baseline:object|null, thresholds:{blink:number, mouth:number}}>}
   */
  function calibrate(ms = 4000) {
    finishCalibration();
    return new Promise((resolve) => {
      calibration = { samples: [], resolve, timer: setTimeout(finishCalibration, ms) };
    });
  }

  /** Включить срабатывание нарушений. */
  function arm() {
    consecutive = 0;
    armedAt = performance.now();
    mouthSeenOpen = false;
    armed = true;
  }

  /** Выключить срабатывание (цикл и индикатор лица продолжают работать). */
  function disarm() {
    consecutive = 0;
    armed = false;
  }

  return {
    start,
    stop,
    calibrate,
    arm,
    disarm,
    get running() {
      return running;
    },
    get armed() {
      return armed;
    },
    get facePresent() {
      return facePresent;
    },
    get thresholds() {
      return { ...thresholds };
    },
    get baseline() {
      return baseline ? { ...baseline } : null;
    },
    get calibrated() {
      return calibrated;
    },
  };
}

/** Optional normalized focus areas from the same landmarks as the exact violation JPEG. */
export function replayRegionsFromLandmarks(landmarks) {
  function region(indices, padX, padY) {
    const points = indices.map(index => landmarks?.[index]);
    if (points.some(point => !point || !Number.isFinite(point.x) || !Number.isFinite(point.y))) return null;
    const minX = Math.min(...points.map(point => point.x));
    const maxX = Math.max(...points.map(point => point.x));
    const minY = Math.min(...points.map(point => point.y));
    const maxY = Math.max(...points.map(point => point.y));
    const width = Math.max(0.08, maxX - minX);
    const height = Math.max(0.035, maxY - minY);
    const x = clamp(minX - width * padX, 0, 1);
    const y = clamp(minY - height * padY, 0, 1);
    return { x, y, width: Math.min(1, maxX + width * padX) - x,
      height: Math.min(1, maxY + height * padY) - y };
  }
  return {
    eyes: region([...LEFT_EYE, ...RIGHT_EYE], 0.22, 0.8),
    mouth: region([...MOUTH_CORNERS, ...MOUTH_PAIRS.flat()], 0.35, 0.6),
  };
}
